import type { FilterOption } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useGeoAreas } from '../geography/useGeoAreas.ts';
import { PERIOD_PRESETS } from '../report-period.ts';
import { useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { companyFilterOptionsOf, useCompanies } from '../companies.ts';
import { UNASSIGNED_CARRIER, type OrderFilters } from './order-filters.ts';

export type OrderFilterOptions = Readonly<Record<keyof OrderFilters, readonly FilterOption[]>>;

export const useOrderFilterOptions = (api: TypedClient, shipping: ShippingClient): OrderFilterOptions => {
  const { t, locale } = usePageContext();
  const carriers = useCarriers(shipping).data?.items ?? [];
  const governorates = useGeoAreas(api).data?.governorates ?? [];
  const companies = useCompanies(api);
  return {
    carrier: [
      ...carriers.map((carrier) => ({ value: carrier.code, label: carrier.name })),
      { value: UNASSIGNED_CARRIER, label: t('orders.carrierUnassigned') },
    ],
    governorate: [...governorates]
      .sort((left, right) => left.name.localeCompare(right.name, locale))
      .map((governorate) => ({ value: governorate.code, label: governorate.name })),
    company: companyFilterOptionsOf(companies),
    period: PERIOD_PRESETS.map((preset) => ({ value: preset, label: t(`period.${preset}`) })),
  };
};

export const chosenLabelsOf = (filters: OrderFilters, options: OrderFilterOptions): readonly string[] =>
  (Object.keys(options) as (keyof OrderFilters)[]).flatMap((key) => {
    const label = options[key].find((option) => option.value === filters[key])?.label;
    return label === undefined ? [] : [label];
  });
